/**
 * Ending a counter slab on a seam at a corner it shares with another run.
 *
 * At an oblique corner the two runs' slabs overhang their cabinets, and two
 * square-ended slabs cross by that overhang — a sliver of one lying on the
 * other, and a corner piece stopping at the cabinets' fronts an inch short of
 * the counter's. Each slab ends instead where the two FRONTS cross
 * (`kitchen-assembly/buildCornerFillCountertop.slabSeams`), and the corner
 * piece takes what is between. Two places do it: the scan pass at every
 * oblique junction (`functions/features/post-processing/oblique-junction-
 * finish.js`, processStructure 2.377.0) and a drawn run at a bend through an
 * island leg (`bentRunFill.seamBend`). Both cut a slab here.
 *
 * Pure. No I/O.
 */
const { getObjectCorners } = require('../collision/geometry');
const { clipToHalfPlane } = require('../geometry/halfPlaneClip');

/** Below this a difference is rounding, not a lip or a reach. */
const EPS_M = 1e-3;

/** Signed extent of a footprint along `dir`, measured from `origin`. */
function spanAlong(obj, origin, dir) {
  let near = Infinity;
  let far = -Infinity;
  for (const c of getObjectCorners(obj)) {
    const t = (c.x - origin.x) * dir.x + (c.z - origin.z) * dir.z;
    if (t < near) near = t;
    if (t > far) far = t;
  }
  return { near, far };
}

const latOf = (o) => {
  const ry = o.rotation?.y ?? 0;
  return { x: Math.cos(ry), z: -Math.sin(ry) };
};

/** Box `o` lies along `dir` (its local X parallel to the leg). */
const alongLeg = (o, dir) => {
  const lat = latOf(o);
  return Math.abs(lat.x * dir.z - lat.z * dir.x) < 0.02;
};

/**
 * Slab `o` cut back so it begins `seam` metres along `dir` from `point`, its
 * far end kept. A slab outlined by `polygonCorners` (an angled end's) loses
 * the same strip of its outline. Unchanged — the same object — when it
 * already begins there or beyond, or would not reach past the seam at all.
 *
 * @returns {{ slab: Object, cut: number }} `cut`: metres taken off, 0 when
 *   unchanged
 */
function cutSlabTo(o, point, dir, seam) {
  const { near, far } = spanAlong(o, point, dir);
  const cut = seam - near;
  if (cut <= EPS_M || far - seam <= EPS_M) return { slab: o, cut: 0 };
  const slab = {
    ...o,
    center: {
      ...o.center,
      x: o.center.x + (dir.x * cut) / 2,
      z: o.center.z + (dir.z * cut) / 2,
    },
    dimensions: { ...o.dimensions, x: far - seam },
  };
  if (Array.isArray(o.polygonCorners) && o.polygonCorners.length >= 3) {
    const kept = clipToHalfPlane(
      o.polygonCorners.map((p) => ({ x: p.x, z: p.z })),
      (p) => seam - ((p.x - point.x) * dir.x + (p.z - point.z) * dir.z)
    );
    if (kept.length >= 3) slab.polygonCorners = kept;
  }
  return { slab, cut };
}

module.exports = { EPS_M, spanAlong, alongLeg, cutSlabTo };
